import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { filter, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthProvider } from '@services/auth.providers';
import { FeatureFlagModel } from '@generated/FeatureFlags/feature-flag-model';
import { AvailableFeatureFlags } from '@generated/SmartMoving/Core/Data/Settings/available-feature-flags';
import { RegionService } from 'src/app/core/region.service';

@Injectable()
export class AuthorizationHttpInterceptor implements HttpInterceptor {
  private useBetaUrl = false;
  private hasCheckedBetaFlag = false;
  private cacheBust = '';

  constructor(private readonly injector: Injector) {
    this.setCacheBust();
  }

  private setCacheBust() {
    this.cacheBust = (Math.random() + 1).toString(36).substring(7);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    const headers = req.headers;
    if (headers.has('skipAuth')) {
      // removing this header before forwarding request
      const interceptOptions = {
        headers: req.headers.delete('skipAuth')
      };
      req = req.clone(interceptOptions);
      return next.handle(req);
    }

    const auth = this.injector.get(AuthProvider);

    if (req.url.includes(auth.authURL)) {
      // On a physical device, not as simple as "refresh the page", so lets reset everything if they're logging in.
      this.hasCheckedBetaFlag = false;
      this.useBetaUrl = false;
      this.setCacheBust();
    }

    if (auth.getToken() && !this.hasCheckedBetaFlag) {
      // Set the flag immediately, which prevents recursion
      this.hasCheckedBetaFlag = true;
      const httpClient = this.injector.get(HttpClient);
      const regionService = this.injector.get(RegionService);
      httpClient.get<FeatureFlagModel>(`${regionService.getApiBaseUrl()}/features/has-access-to/${AvailableFeatureFlags.BetaFeatures}?cacheBust=${this.cacheBust}`)
          .pipe(filter(x => x.hasAccess))
          .subscribe(() => this.runBetaCheck(httpClient));
    }

    const interceptorOptions = {
      url: this.isResolvedUrl(req.url) ? req.url : this.getUrl(req.url, auth.authURL),
      headers: req.headers.set('Authorization', `Bearer ${auth.getToken()}`)
    };

    if (interceptorOptions.url.includes('ngrok')) {
      interceptorOptions.headers = interceptorOptions.headers.set('ngrok-skip-browser-warning', 'true');
    }
    const authReq = req.clone(interceptorOptions);

    return next
        .handle(authReq)
        .pipe(catchError(err => {
          if (!authReq.url.includes(auth.authURL) && err.status === 401) {
            // noinspection JSIgnoredPromiseFromCall
            auth.logout();
          }
          return throwError(err);
        }));
  }

  // Created by Claude Code AI Agent
  // ApiClientService already prefixes environment.apiUrl. Under forge that base is a relative
  // path, so an absolute-URL check alone would prefix it a second time.
  private isResolvedUrl(url: string) {
    if (url.startsWith('http')) {
      return true;
    }

    const regionService = this.injector.get(RegionService);
    return [regionService.getApiBaseUrl(), regionService.getApiBetaBaseUrl()]
        .some(base => base && url.startsWith(`${base}/`));
  }

  // Created by Claude Code AI Agent
  // Multi-region: route requests to the regional API resolved by RegionService.
  // Falls back to environment.apiUrl/apiBetaUrl when no region is set — older
  // installs / launches without a `region` query param continue to work.
  private getUrl(urlFragment: string, authUrl: string) {
    const regionService = this.injector.get(RegionService);
    if (urlFragment.includes(authUrl)) {
      // To be safe, always use the old API when logging in, so they can reset reliably if needed.
      return `${regionService.getApiBaseUrl()}/${urlFragment}`;
    }

    return `${this.useBetaUrl ? regionService.getApiBetaBaseUrl() : regionService.getApiBaseUrl()}/${urlFragment}`;
  }

  private runBetaCheck(httpClient: HttpClient) {
    // in this case, we actually want to call the base url and not the pre-move beta url, because it doesn't have the ping endpoint
    const regionService = this.injector.get(RegionService);
    httpClient.get(`${regionService.getApiBaseUrl()}/ping?cacheBust=${this.cacheBust}`,
        { responseType: 'text' })
        .subscribe({
          next: () => this.useBetaUrl = true,
          // If it's blocked by their firewall, or any other issue, just fallback to the normal API.
          error: () => this.useBetaUrl = false,
        });
  }
}
